import {
  TicketValidationDTO,
  TransportAssoc,
  TransportAssocEntity,
  TransportEntity,
  TtaSave,
  useCreateTtaDeliverySplitsMutation,
  useCreateTtaPickupSplitsMutation,
  useDeleteTtaAllMutation,
  useDeleteTtaMutation,
  useSaveTtaMutation,
  useUndoTtaSplitsMutation,
  useValidateTtaMutation, useValidateTtasMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import { ResultTypeFrom } from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UseTtaMutationProps = {}

export type UseTtaMutationResult = {
  associate: (pickup: TransportEntity, delivery: TransportEntity, onSuccess: (() => void) | undefined) => void
  undoSplits: (ttas: TransportAssocEntity[], onSuccess: (() => void) | undefined) => void
  createPickupSplits: (ttas: TransportAssocEntity[], onSuccess: (() => void) | undefined) => void
  createDeliverySplits: (ttas: TransportAssocEntity[], onSuccess: (() => void) | undefined) => void
  saveTta: (tta: TtaSave, onSuccess: (() => void) | undefined) => void
  deleteTta: (ttaId: string, onSuccess: (() => void) | undefined) => void
  deleteTtaAll: (ttaId: string, onSuccess: (() => void) | undefined) => void
  dispatchValidate: (tta: TransportAssocEntity, value: boolean, onSuccess: (() => void) | undefined) => void
  dispatchValidateAll: (tta: TransportAssocEntity[], value: boolean, onSuccess: (() => void) | undefined) => void
  isSaving?: boolean
}

export const useTtaMutation = (props: UseTtaMutationProps = {}): UseTtaMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_saving, _setSaving] = useDebouncedState(false, 100)
  const [_deleting, _setDeleting] = useDebouncedState(false, 100)
  const [_deletingTicket, _setDeletingTicket] = useDebouncedState(false, 100)

  // possible mutations
  const [_saveTta, {isLoading: isSaving}] = useSaveTtaMutation()
  const [_deleteTta, {isLoading: isDeleting}] = useDeleteTtaMutation()
  const [_deleteTtaAll, {isLoading: isDeletingTicket}] = useDeleteTtaAllMutation()
  const [_validateTta, {isLoading: isValidating}] = useValidateTtaMutation()
  const [_validateTtas, {isLoading: isValidatingAll}] = useValidateTtasMutation()
  const [_undoSplits, {isLoading: isUndoingSplits}] = useUndoTtaSplitsMutation()
  const [_createPickupSplits, {isLoading: isCreatingPickupSplits}] = useCreateTtaPickupSplitsMutation()
  const [_createDeliverySplits, {isLoading: isCreatingDeliverySplits}] = useCreateTtaDeliverySplitsMutation()

  useEffect(() => {
    _setSaving(isSaving || isValidating || isValidatingAll || isUndoingSplits || isCreatingPickupSplits || isCreatingDeliverySplits)
  }, [isSaving, _setSaving, isValidating, isValidatingAll, isUndoingSplits, isCreatingPickupSplits, isCreatingDeliverySplits])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    _setDeletingTicket(isDeletingTicket)
  }, [isDeletingTicket, _setDeletingTicket])

  useEffect(() => {
    if (_saving) {
      notifications.show({
        id: "useTtaUpdate_saving",
        title: t("common.status.saving", "Saving") + " " + t("common.entity.tta.alt", "Ticket"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useTtaUpdate_saving")
    }
  }, [_saving, t])

  useEffect(() => {
    if (_deletingTicket) {
      notifications.show({
        id: "useTtaUpdate_deletingTicket",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.tta.alt", "Ticket"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useTtaUpdate_deletingTicket")
    }
  }, [_deletingTicket, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useTtaUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.tta", "Transport Assocation"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useTtaUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleSaveResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.tta", "Ticket") + " " + t("common.status.saved", "Saved"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.tta", "Transport Association") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
        })
      );
  }, [t])

  const handleTicketDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.tta.alt", "Ticket") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
        })
      );
  }, [t])

  const saveTta = useCallback((tta: TtaSave, onSuccess: (() => void) | undefined) => {
    const promise = _saveTta(tta).unwrap()
    handleSaveResult(promise, onSuccess)
  }, [_saveTta, handleSaveResult])

  const associate = useCallback((pickup: TransportEntity, delivery: TransportEntity, onSuccess: (() => void) | undefined) => {
    const ttaSave: TtaSave = {
      pickupTransport: pickup.asTypeValue(),
      deliveryTransport: delivery.asTypeValue(),
      category: pickup.category?.code ?? delivery.category?.code,
      regionId: pickup.regionId ?? delivery.regionId,
    }

    saveTta(ttaSave, onSuccess)
  }, [saveTta])

  const validateTta = useCallback((tta: TransportAssocEntity, dto: TicketValidationDTO, onSuccess: (() => void) | undefined) => {
    const promise = _validateTta({id: tta.id, database: tta.database, validation: dto}).unwrap()
    handleSaveResult(promise, onSuccess)
  }, [_validateTta, handleSaveResult])

  const validateTtas = useCallback((ttas: TransportAssocEntity[], dto: TicketValidationDTO, onSuccess: (() => void) | undefined) => {
    const validations = ttas.map(tta => ({ticketId: tta.id, serviceLookup: tta.database, validationDTO: dto}))
    const promise = _validateTtas({validations: validations}).unwrap()
    handleSaveResult(promise, onSuccess)
  }, [_validateTtas, handleSaveResult])

  const dispatchValidateTta = useCallback((tta: TransportAssocEntity, value: boolean, onSuccess: (() => void) | undefined) => {
    validateTta(tta, {dispatchValidated: value}, onSuccess)
  }, [validateTta])

  const dispatchValidateTtas = useCallback((ttas: TransportAssocEntity[], value: boolean, onSuccess: (() => void) | undefined) => {
    const dto = {dispatchValidated: value}
    validateTtas(ttas, dto, onSuccess)
  }, [validateTtas])

  const undoSplits = useCallback((ttas: TransportAssocEntity[], onSuccess: (() => void) | undefined) => {
    const payload: TransportAssoc[] = ttas.map(tta => tta.asTypeValue())
    const promise = _undoSplits(payload).unwrap()
    handleSaveResult(promise, onSuccess)
  }, [_undoSplits, handleSaveResult])

  const createPickupSplits = useCallback((ttas: TransportAssocEntity[], onSuccess: (() => void) | undefined) => {
    const payload: TransportAssoc[] = ttas.map(tta => tta.asTypeValue())
    const promise = _createPickupSplits(payload).unwrap()
    handleSaveResult(promise, onSuccess)
  }, [_createPickupSplits, handleSaveResult])

  const createDeliverySplits = useCallback((ttas: TransportAssocEntity[], onSuccess: (() => void) | undefined) => {
    const payload: TransportAssoc[] = ttas.map(tta => tta.asTypeValue())
    const promise = _createDeliverySplits(payload).unwrap()
    handleSaveResult(promise, onSuccess)
  }, [_createDeliverySplits, handleSaveResult])

  const handleDeleteConfirm = useCallback((ttaId: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteTta({id: ttaId}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteTta, handleDeleteResult])

  const handleDeleteTicketConfirm = useCallback((ttaId: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteTtaAll({id: ttaId}).unwrap()
    handleTicketDeleteResult(promise, onSuccess)
  }, [_deleteTtaAll, handleTicketDeleteResult])

  const deleteTta = useCallback((ttaId: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.tta", "Transport Assocation"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.tta.confirm", "Are you sure you want to delete this transport assocation?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(ttaId, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  const deleteTicket = useCallback((ttaId: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.tta.alt", "Ticket"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.tta.alt.confirm", "Are you sure you want to delete this ticket?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteTicketConfirm(ttaId, onSuccess)
      },
    })
  }, [handleDeleteTicketConfirm, t])

  return {
    associate: associate,
    undoSplits: undoSplits,
    createPickupSplits: createPickupSplits,
    createDeliverySplits: createDeliverySplits,
    saveTta: saveTta,
    deleteTta: deleteTta,
    deleteTtaAll: deleteTicket,
    dispatchValidate: dispatchValidateTta,
    dispatchValidateAll: dispatchValidateTtas,
    isSaving: _saving,
  }
}
